import { useEffect } from 'react';

/**
 * Lightweight per-page SEO hook (no react-helmet dependency).
 * Sets <title>, meta description, canonical link and optional JSON-LD.
 *
 * Usage:
 *   useSeo({
 *     title: 'Contratto Locazione Turistica · Guida 2026',
 *     description: '...',
 *     canonical: 'https://contratti.vitadahost.it/guide/contratto-locazione-turistica',
 *     jsonLd: { '@context': 'https://schema.org', '@type': 'Article', ... }
 *   });
 */
export function useSeo({ title, description, canonical, jsonLd, image, type = 'article' }) {
  useEffect(() => {
    if (title) {
      const prev = document.title;
      document.title = title;
      return () => { document.title = prev; };
    }
  }, [title]);

  useEffect(() => {
    const cleanups = [];

    const upsertMeta = (selector, attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (created) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      cleanups.push(() => {
        if (created && el.parentNode) el.parentNode.removeChild(el);
        else if (prev != null) el.setAttribute('content', prev);
      });
    };

    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    if (image) {
      upsertMeta('meta[property="og:image"]', 'property', 'og:image', image);
      upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);
    }

    // canonical
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      const created = !link;
      if (created) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      const prev = link.getAttribute('href');
      link.setAttribute('href', canonical);
      cleanups.push(() => {
        if (created && link.parentNode) link.parentNode.removeChild(link);
        else if (prev != null) link.setAttribute('href', prev);
      });
    }

    // JSON-LD
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.useSeo = 'page';
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      cleanups.push(() => {
        if (script.parentNode) script.parentNode.removeChild(script);
      });
    }

    return () => cleanups.forEach((fn) => { try { fn(); } catch { /* noop */ } });
  }, [title, description, canonical, image, type, jsonLd]);
}

export default useSeo;
